import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useDataCache } from "../context/DataCacheContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Briefcase, Plus, Pencil, Trash2, Loader2, User, Building2, Search, Filter, ExternalLink, CalendarIcon, Download } from "lucide-react";
import { DateAutoInput } from "../components/DateAutoInput";
import { toast } from "sonner";
import axios from "axios";
import { format, parse } from "date-fns";
import { ro } from "date-fns/locale";
import { API } from "../config/api";

const JUDETE_ROMANIA = [
  "Alba", "Arad", "Argeș", "Bacău", "Bihor", "Bistrița-Năsăud", "Botoșani", "Brașov",
  "Brăila", "București", "Buzău", "Caraș-Severin", "Călărași", "Cluj", "Constanța",
  "Covasna", "Dâmbovița", "Dolj", "Galați", "Giurgiu", "Gorj", "Harghita", "Hunedoara",
  "Ialomița", "Iași", "Ilfov", "Maramureș", "Mehedinți", "Mureș", "Neamț", "Olt",
  "Prahova", "Satu Mare", "Sălaj", "Sibiu", "Suceava", "Teleorman", "Timiș", "Tulcea",
  "Vaslui", "Vâlcea", "Vrancea"
];

const initialFormState = {
  client_type: "",
  // Persoana fizica
  nume: "",
  cnp: "",
  data_nasterii: "",
  // Persoana juridica
  denumire: "",
  cod_fiscal: "",
  numar_orc: "",
  reprezentant_legal: "",
  // Common
  adresa: "",
  localitate: "",
  judet: "",
  tara: "Romania",
  cont_bancar: "",
  banca: "",
  telefon: "",
  email: "",
};

export default function ClientsPage() {
  const { getAuthHeader } = useAuth();
  const { fetchClients: fetchCachedClients, invalidateCache } = useDataCache();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [fetchingAnaf, setFetchingAnaf] = useState(false);
  const searchTimeoutRef = useRef(null);

  // ============ VALIDARE & FORMATARE HELPERS ============

  // Validare CNP românesc (13 cifre + cifră de control)
  const validateCNP = (cnp) => {
    if (!cnp || cnp.length !== 13) return false;
    if (!/^\d{13}$/.test(cnp)) return false;
    const weights = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
    let sum = 0;
    for (let i = 0; i < 12; i++) sum += parseInt(cnp[i]) * weights[i];
    let control = sum % 11;
    if (control === 10) control = 1;
    return control === parseInt(cnp[12]);
  };

  // Extrage data nașterii din CNP
  const extractBirthDateFromCNP = (cnp) => {
    if (!cnp || cnp.length < 7) return null;
    const s = parseInt(cnp[0]);
    let year = parseInt(cnp.substring(1, 3));
    const month = cnp.substring(3, 5);
    const day = cnp.substring(5, 7);
    if ([1, 2].includes(s)) year += 1900;
    else if ([3, 4].includes(s)) year += 1800;
    else if ([5, 6].includes(s)) year += 2000;
    else if ([7, 8].includes(s)) year += 2000; // rezident
    else return null;
    const dateStr = `${year}-${month}-${day}`;
    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) return null;
    return { dateStr, displayStr: `${day}/${month}/${year}` };
  };

  // Validare IBAN românesc
  const validateIBAN = (iban) => {
    if (!iban) return true; // optional field
    const clean = iban.replace(/\s/g, '').toUpperCase();
    if (!/^RO\d{2}[A-Z]{4}[A-Z0-9]{16}$/.test(clean)) return false;
    // Mod 97 check
    const rearranged = clean.slice(4) + clean.slice(0, 4);
    const numeric = rearranged.replace(/[A-Z]/g, (ch) => (ch.charCodeAt(0) - 55).toString());
    let remainder = '';
    for (const digit of numeric) {
      remainder += digit;
      remainder = (parseInt(remainder) % 97).toString();
    }
    return parseInt(remainder) === 1;
  };

  // Format telefon românesc
  const formatPhone = (val) => {
    let digits = val.replace(/[^0-9+]/g, '');
    // Permite +40 prefix
    if (digits.startsWith('+40')) {
      const rest = digits.slice(3).replace(/\D/g, '');
      if (rest.length <= 9) {
        // Format: +40 7xx xxx xxx
        let formatted = '+40';
        if (rest.length > 0) formatted += ' ' + rest.slice(0, 3);
        if (rest.length > 3) formatted += ' ' + rest.slice(3, 6);
        if (rest.length > 6) formatted += ' ' + rest.slice(6, 9);
        return formatted;
      }
      return digits.slice(0, 13);
    }
    // Format: 07xx xxx xxx
    const d = digits.replace(/\D/g, '');
    if (d.startsWith('0') && d.length <= 10) {
      let formatted = d.slice(0, 4);
      if (d.length > 4) formatted += ' ' + d.slice(4, 7);
      if (d.length > 7) formatted += ' ' + d.slice(7, 10);
      return formatted;
    }
    return digits;
  };

  // Handle CNP change: doar cifre + auto-completare data nașterii
  const handleCNPChange = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 13);
    const updates = { cnp: digits };
    // Auto-completare data nașterii la 7+ cifre
    if (digits.length >= 7) {
      const birth = extractBirthDateFromCNP(digits);
      if (birth) {
        updates.data_nasterii = birth.dateStr;
      }
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Handle CUI change: doar cifre (prefix RO opțional)
  const handleCUIChange = (val) => {
    // Permite "RO" la început + cifre
    let cleaned = val.toUpperCase();
    if (cleaned.startsWith('RO')) {
      cleaned = 'RO' + cleaned.slice(2).replace(/\D/g, '');
    } else {
      cleaned = cleaned.replace(/[^0-9R]/gi, '');
      // Dacă a introdus doar R, permite
      if (cleaned === 'R') cleaned = 'R';
      else if (cleaned.startsWith('R') && !cleaned.startsWith('RO')) cleaned = cleaned.replace(/\D/g, '');
      else cleaned = cleaned.replace(/\D/g, '');
    }
    setFormData(prev => ({ ...prev, cod_fiscal: cleaned }));
  };

  // Funcție pentru preluare date de la ANAF pe baza CUI
  const fetchAnafData = async () => {
    if (!formData.cod_fiscal) {
      toast.error("Introduceți codul fiscal");
      return;
    }
    
    setFetchingAnaf(true);
    try {
      const response = await axios.get(`${API}/clients/anaf/${formData.cod_fiscal}`, getAuthHeader());
      const data = response.data;
      setFormData(prev => ({
        ...prev,
        denumire: data.denumire || prev.denumire,
        cod_fiscal: data.cod_fiscal || prev.cod_fiscal,
        adresa: data.adresa || prev.adresa,
        localitate: data.localitate || prev.localitate,
        judet: data.judet || prev.judet,
        tara: data.tara || prev.tara,
        numar_orc: data.numar_orc || prev.numar_orc,
        telefon: data.telefon || prev.telefon,
        cont_bancar: data.iban || prev.cont_bancar,
      }));
      toast.success(`Date preluate: ${data.denumire}`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Serviciul ANAF nu este disponibil";
      toast.error(
        <div className="space-y-1">
          <p>{msg}</p>
          <a 
            href={`https://mfinante.gov.ro/info-pj-selectie?cui=${formData.cod_fiscal}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline text-xs flex items-center gap-1"
          >
            Verifică manual pe mfinante.gov.ro <ExternalLink className="w-3 h-3" />
          </a>
        </div>,
        { duration: 8000 }
      );
    } finally {
      setFetchingAnaf(false);
    }
  };

  const fetchClients = useCallback(async (forceRefresh = false) => {
    try {
      const data = await fetchCachedClients(forceRefresh);
      // Sortare alfabetică după display_name
      const sortedClients = [...data].sort((a, b) => 
        (a.display_name || '').localeCompare(b.display_name || '', 'ro')
      );
      setClients(sortedClients);
    } catch (error) {
      toast.error("Eroare la încărcarea clienților");
    } finally {
      setLoading(false);
    }
  }, [fetchCachedClients]);

  // Initial load
  useEffect(() => {
    fetchClients();
  }, []);

  // Normalize Romanian diacritics for search (a=ă=â, s=ș, t=ț, i=î)
  const normalizeForSearch = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[ăâ]/g, 'a')
      .replace(/[î]/g, 'i')
      .replace(/[ș]/g, 's')
      .replace(/[ț]/g, 't');
  };

  // Filtered clients (computed locally, no API call)
  const filteredClients = useMemo(() => {
    let result = clients;
    
    // Filter by type
    if (filterType !== "all") {
      result = result.filter(c => c.client_type === filterType);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = normalizeForSearch(searchQuery);
      result = result.filter(c => 
        normalizeForSearch(c.display_name).includes(query) ||
        normalizeForSearch(c.email).includes(query) ||
        (c.cnp || '').includes(searchQuery) ||
        (c.cod_fiscal || '').includes(searchQuery) ||
        (c.telefon || '').includes(searchQuery)
      );
    }
    
    return result;
  }, [clients, filterType, searchQuery]);

  const handleSelectType = (type) => {
    setFormData({ ...initialFormState, client_type: type });
    setTypeDialogOpen(false);
    setDialogOpen(true);
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setSelectedClient(client);
      setFormData({
        client_type: client.client_type,
        nume: client.nume || "",
        cnp: client.cnp || "",
        data_nasterii: client.data_nasterii || "",
        denumire: client.denumire || "",
        cod_fiscal: client.cod_fiscal || "",
        numar_orc: client.numar_orc || "",
        reprezentant_legal: client.reprezentant_legal || "",
        adresa: client.adresa || "",
        localitate: client.localitate || "",
        judet: client.judet || "",
        tara: client.tara || "Romania",
        cont_bancar: client.cont_bancar || "",
        banca: client.banca || "",
        telefon: client.telefon || "",
        email: client.email || "",
      });
      // DateAutoInput sincronizează automat cu data existentă
      setDialogOpen(true);
    } else {
      setSelectedClient(null);
      setTypeDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedClient(null);
    setFormData(initialFormState);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on type
    if (formData.client_type === "persoana_fizica") {
      if (!formData.nume || !formData.telefon || !formData.adresa || !formData.localitate) {
        toast.error("Completați toate câmpurile obligatorii (Nume, Telefon, Adresă, Localitate)");
        return;
      }
      
      // Validare CNP (dacă e completat)
      if (formData.cnp && !validateCNP(formData.cnp)) {
        toast.error("CNP invalid. Verificați cele 13 cifre.");
        return;
      }
      
      // Verificare CNP existent (doar dacă e completat)
      if (formData.cnp && !selectedClient) {
        const existingClient = clients.find(c => c.cnp === formData.cnp);
        if (existingClient) {
          toast.error(`Client existent: ${existingClient.display_name} are același CNP`);
          return;
        }
      }
    } else {
      if (!formData.denumire || !formData.cod_fiscal || !formData.telefon || !formData.adresa || !formData.localitate) {
        toast.error("Completați toate câmpurile obligatorii (Denumire, CUI, Telefon, Adresă, Localitate)");
        return;
      }
      
      // Verificare CUI existent
      if (formData.cod_fiscal && !selectedClient) {
        const existingClient = clients.find(c => c.cod_fiscal === formData.cod_fiscal);
        if (existingClient) {
          toast.error(`Client existent: ${existingClient.display_name} are același Cod Fiscal`);
          return;
        }
      }
    }

    // Validare IBAN (comun PF/PJ)
    if (formData.cont_bancar && !validateIBAN(formData.cont_bancar)) {
      toast.error("IBAN invalid. Formatul corect: RO + 2 cifre + 4 litere + 16 caractere (ex: RO49AAAA1B31007593840000)");
      return;
    }

    setSubmitting(true);
    try {
      if (selectedClient) {
        await axios.put(`${API}/clients/${selectedClient.id}`, formData, getAuthHeader());
        toast.success("Client actualizat");
      } else {
        await axios.post(`${API}/clients`, formData, getAuthHeader());
        toast.success("Client creat");
      }
      handleCloseDialog();
      invalidateCache("clients");
      fetchClients(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operațiune eșuată");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    
    try {
      await axios.delete(`${API}/clients/${selectedClient.id}`, getAuthHeader());
      toast.success("Client șters");
      setDeleteDialogOpen(false);
      setSelectedClient(null);
      invalidateCache("clients");
      fetchClients(true);
    } catch (error) {
      toast.error("Eroare la ștergerea clientului");
    }
  };

  const openDeleteDialog = (client) => {
    setSelectedClient(client);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse font-body text-muted-foreground">Se încarcă clienții...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Clienți</h1>
          <p className="font-body text-muted-foreground mt-1">
            Gestionează baza de date clienți
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="export-clients-pdf"
            onClick={async () => {
              try {
                const response = await axios.get(`${API}/export/clients-report`, {
                  ...getAuthHeader(),
                  responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `lista_clienti_${format(new Date(), 'yyyyMMdd')}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success("PDF exportat");
              } catch (error) {
                toast.error("Eroare la export");
              }
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Exportă PDF
          </Button>
          <Button onClick={() => handleOpenDialog()} data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Adaugă Client
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Caută după nume, email, CNP/CIF..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-clients"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48" data-testid="filter-type">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Toate tipurile" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate tipurile</SelectItem>
            <SelectItem value="persoana_fizica">Persoană Fizică</SelectItem>
            <SelectItem value="persoana_juridica">Persoană Juridică</SelectItem>
          </SelectContent>
        </Select>
      </div>
      </div>{/* End Sticky */}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Clienți ({filteredClients.length}{filteredClients.length !== clients.length ? ` din ${clients.length}` : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredClients.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">
                {clients.length === 0 ? "Nu există clienți încă." : "Nu s-au găsit clienți conform filtrelor."}
              </p>
              {clients.length === 0 && (
                <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog()}>
                  Adaugă Primul Client
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-body font-semibold">Tip</TableHead>
                    <TableHead className="font-body font-semibold">Nume/Denumire</TableHead>
                    <TableHead className="font-body font-semibold">CNP/CIF</TableHead>
                    <TableHead className="font-body font-semibold">Localitate</TableHead>
                    <TableHead className="font-body font-semibold">Telefon</TableHead>
                    <TableHead className="font-body font-semibold">Email</TableHead>
                    <TableHead className="font-body font-semibold text-right">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.id} className="table-row-hover" data-testid={`client-row-${client.id}`}>
                      <TableCell>
                        <Badge variant="outline" className={`font-body text-xs ${
                          client.client_type === "persoana_fizica" 
                            ? "bg-blue-100 text-blue-700 border-blue-300" 
                            : "bg-amber-100 text-amber-700 border-amber-300"
                        }`}>
                          {client.client_type === "persoana_fizica" ? (
                            <><User className="w-3 h-3 mr-1" />PF</>
                          ) : (
                            <><Building2 className="w-3 h-3 mr-1" />PJ</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-body font-medium">
                        {client.display_name}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {client.client_type === "persoana_fizica" ? (client.cnp || "-") : client.cod_fiscal}
                      </TableCell>
                      <TableCell className="font-body">
                        {client.localitate}{client.judet ? `, ${client.judet}` : ""}{client.tara && client.tara !== "Romania" ? `, ${client.tara}` : ""}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{client.telefon}</TableCell>
                      <TableCell className="font-mono text-sm">{client.email}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(client)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => openDeleteDialog(client)}
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Type Selection Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="client-type-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Selectează Tipul de Client</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <Button
              variant="outline"
              className="h-32 flex flex-col items-center justify-center gap-3 hover:border-primary hover:bg-primary/5"
              onClick={() => handleSelectType("persoana_fizica")}
              data-testid="select-persoana-fizica"
            >
              <User className="w-10 h-10 text-primary" />
              <span className="font-body font-medium">Persoană Fizică</span>
            </Button>
            <Button
              variant="outline"
              className="h-32 flex flex-col items-center justify-center gap-3 hover:border-primary hover:bg-primary/5"
              onClick={() => handleSelectType("persoana_juridica")}
              data-testid="select-persoana-juridica"
            >
              <Building2 className="w-10 h-10 text-primary" />
              <span className="font-body font-medium">Persoană Juridică</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {formData.client_type === "persoana_fizica" ? (
                <><User className="w-5 h-5" /> {selectedClient ? "Editează" : "Adaugă"} Persoană Fizică</>
              ) : (
                <><Building2 className="w-5 h-5" /> {selectedClient ? "Editează" : "Adaugă"} Persoană Juridică</>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Persoana Fizica Fields */}
            {formData.client_type === "persoana_fizica" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nume" className="font-body">Nume *</Label>
                  <Input
                    id="nume"
                    value={formData.nume}
                    onChange={(e) => setFormData({ ...formData, nume: e.target.value })}
                    placeholder="Popescu Ion"
                    data-testid="input-nume"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnp" className="font-body">CNP <span className="text-muted-foreground text-xs">(opțional)</span></Label>
                  <Input
                    id="cnp"
                    value={formData.cnp}
                    onChange={(e) => handleCNPChange(e.target.value)}
                    placeholder="1234567890123"
                    maxLength={13}
                    data-testid="input-cnp"
                    className={formData.cnp && formData.cnp.length === 13 && !validateCNP(formData.cnp) ? "border-red-400" : ""}
                  />
                  {formData.cnp && formData.cnp.length === 13 && !validateCNP(formData.cnp) && (
                    <p className="text-xs text-red-500">CNP invalid</p>
                  )}
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="data_nasterii" className="font-body">Data Nașterii (pentru felicitări)</Label>
                  <div className="flex gap-1">
                    <DateAutoInput
                      value={formData.data_nasterii}
                      onChange={(val) => setFormData({ ...formData, data_nasterii: val })}
                      className="flex-1"
                      data-testid="input-data-nasterii"
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="icon" type="button" className="shrink-0">
                          <CalendarIcon className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={formData.data_nasterii ? parse(formData.data_nasterii, "yyyy-MM-dd", new Date()) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              setFormData({ ...formData, data_nasterii: format(date, "yyyy-MM-dd") });
                            } else {
                              setFormData({ ...formData, data_nasterii: "" });
                            }
                          }}
                          locale={ro}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            )}

            {/* Persoana Juridica Fields */}
            {formData.client_type === "persoana_juridica" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cod_fiscal" className="font-body">Cod Fiscal / CUI *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="cod_fiscal"
                        value={formData.cod_fiscal}
                        onChange={(e) => handleCUIChange(e.target.value)}
                        placeholder="12345678"
                        data-testid="input-cod-fiscal"
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={fetchAnafData}
                        disabled={fetchingAnaf || !formData.cod_fiscal}
                        data-testid="btn-fetch-anaf"
                        title="Preia date de la ANAF"
                      >
                        {fetchingAnaf ? <Loader2 className="w-4 h-4 animate-spin" /> : "ANAF"}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (formData.cod_fiscal) {
                            window.open(`https://mfinante.gov.ro/info-pj-selectie?cui=${formData.cod_fiscal}`, '_blank');
                          }
                        }}
                        disabled={!formData.cod_fiscal}
                        title="Verifică pe mfinante.gov.ro"
                        data-testid="btn-mfinante"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="denumire" className="font-body">Denumire *</Label>
                    <Input
                      id="denumire"
                      value={formData.denumire}
                      onChange={(e) => setFormData({ ...formData, denumire: e.target.value })}
                      placeholder="SC Exemplu SRL"
                      data-testid="input-denumire"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="numar_orc" className="font-body">Număr ORC</Label>
                    <Input
                      id="numar_orc"
                      value={formData.numar_orc}
                      onChange={(e) => setFormData({ ...formData, numar_orc: e.target.value })}
                      placeholder="J40/1234/2020"
                      data-testid="input-numar-orc"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reprezentant_legal" className="font-body">Reprezentant Legal</Label>
                    <Input
                      id="reprezentant_legal"
                      value={formData.reprezentant_legal}
                      onChange={(e) => setFormData({ ...formData, reprezentant_legal: e.target.value })}
                      placeholder="Popescu Ion"
                      data-testid="input-reprezentant"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Common Fields */}
            <div className="space-y-2">
              <Label htmlFor="adresa" className="font-body">Adresă *</Label>
              <Input
                id="adresa"
                value={formData.adresa}
                onChange={(e) => setFormData({ ...formData, adresa: e.target.value })}
                placeholder="Str. Exemplu, Nr. 1, Bl. A, Ap. 10"
                data-testid="input-adresa"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="localitate" className="font-body">Localitate *</Label>
                <Input
                  id="localitate"
                  value={formData.localitate}
                  onChange={(e) => setFormData({ ...formData, localitate: e.target.value })}
                  placeholder="București"
                  data-testid="input-localitate"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="judet" className="font-body">Județ <span className="text-muted-foreground text-xs">(opțional)</span></Label>
                <Select
                  value={formData.judet}
                  onValueChange={(value) => setFormData({ ...formData, judet: value })}
                >
                  <SelectTrigger data-testid="select-judet">
                    <SelectValue placeholder="Selectează județ (opțional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Fără județ --</SelectItem>
                    {JUDETE_ROMANIA.map((judet) => (
                      <SelectItem key={judet} value={judet}>{judet}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tara" className="font-body">Țară</Label>
                <Input
                  id="tara"
                  value={formData.tara}
                  onChange={(e) => setFormData({ ...formData, tara: e.target.value })}
                  placeholder="Romania"
                  data-testid="input-tara"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cont_bancar" className="font-body">Cont Bancar (IBAN)</Label>
                <Input
                  id="cont_bancar"
                  value={formData.cont_bancar}
                  onChange={(e) => setFormData({ ...formData, cont_bancar: e.target.value.toUpperCase().replace(/\s/g, '') })}
                  placeholder="RO49AAAA1B31007593840000"
                  data-testid="input-cont-bancar"
                  maxLength={24}
                  className={formData.cont_bancar && formData.cont_bancar.length >= 24 && !validateIBAN(formData.cont_bancar) ? "border-red-400" : ""}
                />
                {formData.cont_bancar && formData.cont_bancar.length >= 24 && !validateIBAN(formData.cont_bancar) && (
                  <p className="text-xs text-red-500">IBAN invalid</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="banca" className="font-body">Bancă</Label>
                <Input
                  id="banca"
                  value={formData.banca}
                  onChange={(e) => setFormData({ ...formData, banca: e.target.value })}
                  placeholder="Banca Transilvania"
                  data-testid="input-banca"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefon" className="font-body">Telefon *</Label>
                <Input
                  id="telefon"
                  value={formData.telefon}
                  onChange={(e) => setFormData({ ...formData, telefon: formatPhone(e.target.value) })}
                  placeholder="0721 234 567"
                  data-testid="input-telefon"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="font-body">Email (opțional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="client@exemplu.ro"
                  data-testid="input-email"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Anulează
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-client">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Se salvează...
                  </>
                ) : selectedClient ? (
                  "Actualizează"
                ) : (
                  "Creează"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Șterge Client</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Ești sigur că vrei să ștergi clientul {selectedClient?.display_name}? Această acțiune nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-client"
            >
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
